import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';

import { ChevronDown } from "../../files/icons/index.js";
import { Button } from '../components/component.js';
import { EditableDatePicker } from "../components/datePicker.js";
import MaskedInput from "../components/maskedInput.js";
import { sanitizeMBI, isValidMBI } from "../../utils/mbi.js";
import { isCaregiverFieldMissing, MISSING_FIELD_CLASS } from "../../utils/paafRequired.js";

function CaregiverCreate() {
  const context = useContext(AppContext);

  let { id } = useParams();
  let caregiverID = id;
  let createMode = id === "create";

  const navigate = useNavigate();

  // Get URL params
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(null);

  const [form, setForm] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const [patientID, setPatientID] = useState(searchParams.get("patientID"));

  useEffect(() => {
    if (caregiverID != "create") {
      let u = context.addSpiner();
      axios.all([
        axios.get('/api/admin/settings', {
        }),
        axios.post('/api/admin/patients', {
          filter: {
            id: [patientID],
          }
        })
      ]).then(axios.spread((settings, patients) => {
        let patient = patients.data.patients[0];
        let caregiver = patient.caregivers.find((c) => c.id === caregiverID);

        const newForm = {};
        caregiver.fields.forEach((field) => {
          if (field.answers && field.answers.length > 0) {
            newForm[field.code] = field.answers[0];
            //console.log("Field", field.code, "set to", field.answers[0]);
          }
        });
        setForm(newForm);

        setSettings(settings.data);
        setLoading(false);
      })).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      }).finally(() => {
        context.removeSpiner(u);
      });
    } else {
      let u = context.addSpiner();
      axios.all([
        axios.get('/api/admin/settings', {
        }),
      ]).then(axios.spread((settings) => {
        setSettings(settings.data);

        // Initialize form with default values
        // const newForm = {};
        // newForm["dateOfBirth"] = new Date(0);
        // setForm(newForm);

        setLoading(false);
      })).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      }).finally(() => {
        context.removeSpiner(u);
      });
    }
  }, []);

  function renderPages() {
    return settings.caregiverTemplate.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function highlightClass(code) {
    if (formErrors[code]) return " " + MISSING_FIELD_CLASS;
    if (createMode) return "";
    return isCaregiverFieldMissing(code, form) ? " " + MISSING_FIELD_CLASS : "";
  }

  function inputClass(code) {
    return "input sm typo-normal-150 w-100" + highlightClass(code);
  }

  function renderLine(pageID, line, isLastLine) {
    // Line example
    //[{"code": "firstName", "size": 6}, {"code": "lastName", "size": 6}]

    return (
      <div key={"page_"+pageID+"_line_"+line.id} className={"row " + (isLastLine ? "" : "mb-2")}>
        {line.map((lineField, index) => {
          let field = settings.caregiverTemplate.fields.find((f) => f.code === lineField.code);
          const missing = !createMode && isCaregiverFieldMissing(field.code, form);
          const radioMissing = !!highlightClass(field.code);

          return (
            <div key={"page_"+pageID+"_line_"+line.id+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
              <div className={"typo-strong-150" + (missing ? " text-red" : "")}>
                {field.title}:
              </div>
              {field.description && (
                <div className="typo-normal-125 text-muted mb-1">{field.description}</div>
              )}

              {field.type === "text" && !field.format && field.code !== 'medicareBeneficiaryIdentifier' && (
                <input type="text" placeholder="" value={form[field.code]} onChange={(e) => changeField(field.code, e.target.value)} className={inputClass(field.code)} />
              )}

              {field.type === "email" && (
                <input
                  type="email"
                  placeholder=""
                  value={form[field.code] || ""}
                  onChange={(e) => changeField(field.code, e.target.value)}
                  onBlur={() => {
                    const val = form[field.code];
                    setFormErrors(prev => ({ ...prev, [field.code]: val ? !validateEmail(val) : false }));
                  }}
                  className={inputClass(field.code)}
                />
              )}

              {field.code === 'medicareBeneficiaryIdentifier' && (
                <input
                  type="text"
                  value={form[field.code] || ""}
                  onChange={(e) => changeField(field.code, sanitizeMBI(e.target.value))}
                  onBlur={() => {
                    const val = form[field.code];
                    setFormErrors(prev => ({ ...prev, [field.code]: val ? !isValidMBI(val) : false }));
                  }}
                  className={inputClass(field.code)}
                />
              )}

              {field.type === "text" && field.format && field.code !== 'medicareBeneficiaryIdentifier' && (
                <MaskedInput
                  value={form[field.code]}
                  onChange={(value) => changeField(field.code, value)}
                  formatRegex={field.format}
                  className={inputClass(field.code)}
                />
              )}

              {field.type === "date" && (
                <EditableDatePicker 
                  date={form[field.code]}
                  onChange={(e) => changeField(field.code, e)}
                  rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                  className={highlightClass(field.code).trim()}
                />
              )}

              {field.type === "radio" && (
                <div className={radioMissing ? MISSING_FIELD_CLASS + " rounded p-1" : undefined}>
                  {field.options.map((option) => {
                    return (
                      <div key={"radio_"+field.code+"_"+option} className={"item typo-normal-150 me-2 mb-1 " + (form[field.code] == option ? "blue" : "grey")} onClick={(e) => changeField(field.code, option)}>{option}</div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function validateEmail(value) {
    return /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(value);
  }

  function changeField(name, value) {
    // Create a new object to avoid direct mutation
    const newForm = { ...form, [name]: value ? value : null };
    setForm(newForm);
  }

  function validateEmailFields() {
    const emailFields = settings.patientTemplate.fields.filter(f => f.type === "email");
    const errors = {};
    for (const field of emailFields) {
      const val = form[field.code];
      if (val && !validateEmail(val)) {
        errors[field.code] = true;
      }
    }
    setFormErrors(prev => ({ ...prev, ...errors }));
    if (Object.keys(errors).length > 0) {
      context.addMessage("Please enter a valid email address.");
    }
    return Object.keys(errors).length === 0;
  }

  function validateMBIFields() {
    const mbiField = settings.caregiverTemplate.fields.find(f => f.code === "medicareBeneficiaryIdentifier");
    if (!mbiField) return true;
    const val = form[mbiField.code];
    if (val && !isValidMBI(val)) {
      setFormErrors(prev => ({ ...prev, [mbiField.code]: true }));
      context.addMessage("Please enter a valid Medicare Beneficiary Identifier (MBI).");
      return false;
    }
    return true;
  }

  function validateLengthInRole() {
    const years = form["yearsInRole"];
    const months = form["monthsInRole"];
    const yearsEmpty = years === undefined || years === null || String(years).trim() === "";
    const monthsEmpty = months === undefined || months === null || String(months).trim() === "";
    if (yearsEmpty && monthsEmpty) {
      context.addMessage("At least one of years or months in role is required for PAAF. Years must be 1 or more; months 0-11. The other may be left blank.");
      return false;
    }
    return true;
  }

  function createCaregiver() {
    if (!validateEmailFields()) return;
    if (!validateMBIFields()) return;
    if (!validateLengthInRole()) return;
    let u = context.addSpiner();
    const trimmedForm = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

    let url = '/api/admin/patients/createCaregiver';
    if (!createMode) {
      url = '/api/admin/patients/updateCaregiver';
    }

    axios.post(url, {
      id: createMode ? null : caregiverID,
      values: trimmedForm,
      patientID: patientID,
    }).then((response) => {
      navigate('/patient/'+patientID+'/card?tab=caregivers');
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  if (loading) {
    return <></>;
  }

  return (
    <>
      <div className="container">
        <div className="row mb-3">
          <div className="col-xs-12">
            <div className="d-flex justify-content-start align-items-center">
              <span className="typo-hero-small">{createMode ? "New Caregiver" : "Edit Caregiver"}</span>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">
            {renderPages()}

            <div className="d-flex justify-content-end">
              <Button onClick={createCaregiver} color="blue" text={createMode ? "Create" : "Save"} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CaregiverCreate;
